/**
 * Traditional Chinese (Taiwan) for the overseas office (lib/i18n.tsx). Same keys as zh.ts (Simplified,
 * for the factory); Taiwan wording rather than a character-for-character conversion: 登入, 搜尋, 載入,
 * 匯出, 設定, 布料, 貨櫃. A line missing here shows in English.
 *
 * FOR THE REVIEWER: change the Chinese on the right only; keep {placeholders} and "\n" exactly as written.
 *
 * Glossary:
 *   Frame 傘架 · Cover 傘布 · Base 傘座 · Fabric 布料 · Trim 飾邊 · Protective cover 防護罩
 *   Stock 庫存 · On order 已下單（未到貨） · Forecast use 預測用量 · Need to order 需訂購
 *   Order now 立即下單 · Ship sooner 需提前出貨 · Top-up 補貨 · Late PO 逾期 PO · Safety stock 安全庫存
 *   PO (kept as "PO" next to a number) · Container 貨櫃 · Lead time 交期 · Jurupa (warehouse name, kept)
 */
export const ZH_TW: Record<string, string> = {
  // ─── menu and top bar ───
  "Every day": "日常",
  Planning: "規劃",
  Help: "說明",
  Admin: "管理",
  Home: "首頁",
  "To order": "待訂購",
  "for buying": "採購用",
  Arriving: "即將到貨",
  "for receiving": "收貨用",
  Forecast: "預測",
  Trends: "趨勢",
  "what customers choose": "客戶選購情形",
  "Yearly Demand": "年度需求",
  "Forecast accuracy": "預測準確度",
  "How this site works": "網站使用說明",
  "Data status": "資料狀態",
  Search: "搜尋",
  "Guide: how the app works": "使用指南",
  Main: "主選單",
  "Open menu": "開啟選單",
  "Colour theme": "色彩主題",
  Light: "淺色",
  Dark: "深色",
  Auto: "自動",
  "Light theme": "淺色主題",
  "Dark theme": "深色主題",
  "Follow your computer's setting": "依照電腦設定",
  viewer: "檢視者",
  editor: "編輯者",
  admin: "管理員",
  factory: "工廠",
  loading: "載入中",
  "Sign out": "登出",
  "Stock syncs from NetSuite every hour": "庫存每小時從 NetSuite 同步一次",
  "Updated {day}, {time}": "更新於 {day} {time}",
  "{n} overdue": "{n} 項逾期",
  "Search a part, PO or container": "搜尋零件、PO 或貨櫃",
  "Part, PO or container, e.g. alto 9 bronze, 16802, CA-27013": "零件、PO 或貨櫃，例如 alto 9 bronze、16802、CA-27013",

  // ─── part categories and product lines ───
  Frames: "傘架",
  Covers: "傘布",
  Bases: "傘座",
  Fabric: "布料",
  Trim: "飾邊",
  "Protective covers": "防護罩",
  "Solstice covers": "Solstice 傘布",
  "Pacifica Marine covers": "Pacifica Marine 傘布",
  "Sunbrella covers": "Sunbrella 傘布",
  "Other covers": "其他傘布",
  "{code} bases": "{code} 傘座",
  yd: "碼",
  units: "件",

  // ─── dates ───
  "no date": "無日期",
  Jan: "1月", Feb: "2月", Mar: "3月", Apr: "4月", May: "5月", Jun: "6月",
  Jul: "7月", Aug: "8月", Sep: "9月", Oct: "10月", Nov: "11月", Dec: "12月",

  // ─── Home ───
  "Parts only.": "僅限零件。",
  "Finished umbrellas are in NetSuite. Nothing you do here changes NetSuite.": "成品傘請在 NetSuite 查看。在本網站的任何操作都不會更改 NetSuite。",
  Dismiss: "關閉",
  "not yet": "尚未執行",
  "not run yet": "尚未執行",
  "matches ({n} parts checked)": "一致（已核對 {n} 個零件）",
  "{n} parts differ": "{n} 個零件不一致",
  failed: "失敗",
  "Stock from NetSuite {when}": "NetSuite 庫存：{when}",
  "Forecast updated {when}": "預測更新：{when}",
  "NetSuite check: {result}": "NetSuite 核對：{result}",
  "Couldn't load stock and orders:": "無法載入庫存與訂單：",
  Buying: "採購",
  "Open To order →": "開啟待訂購 →",
  "Order now": "立即下單",
  parts: "個零件",
  "They'll run out before a new order could arrive": "新訂單到貨前就會缺貨",
  "Ship sooner": "需提前出貨",
  deliveries: "批到貨",
  "On order, but arriving after the shelf runs empty": "已下單，但到貨時庫存已用完",
  "Top-ups": "補貨",
  "Can wait": "可稍後處理",
  "Not counted: {n} parts Operations doesn't keep in stock ({a} would be in Order now, {b} in Top-ups). Nobody needs to buy them here.":
    "未計入：{n} 個營運部不備庫存的零件（其中 {a} 個原應列入「立即下單」，{b} 個列入「補貨」）。無需在此採購。",
  "Receiving at Jurupa": "Jurupa 收貨",
  "Open Arriving →": "開啟即將到貨 →",
  Overdue: "逾期",
  "POs or containers": "個 PO 或貨櫃",
  "{late} late, {stalled} stalled.": "{late} 項逾期，{stalled} 項停滯。",
  "Most overdue: {ref}, due {date}": "逾期最久：{ref}，應於 {date} 到貨",
  "Oldest line was due {date}": "最早一項應於 {date} 到貨",
  "Arriving this week": "本週到貨",
  "Next: {ref} on {date}": "下一批：{ref}，{date}",
  "Nothing else due this week": "本週沒有其他到貨",
  Stalled: "停滯",
  POs: "個 PO",
  "Over 30 days late and never shipped. Chase or close in NetSuite": "逾期超過 30 天且從未出貨。請在 NetSuite 追蹤或關閉",
  "PO {ref}": "PO {ref}",

  // ─── Forecast ───
  View: "檢視",
  Simple: "精簡",
  "More detail": "詳細",
  "Every column, for planners.": "顯示所有欄位，供規劃人員使用。",
  Show: "顯示",
  Parts: "零件",
  "Needs attention": "需留意",
  "Needs attention · {n} parts": "需留意 · {n} 個零件",
  "Late PO": "逾期 PO",
  "Top-up": "補貨",
  "Over a year of stock": "庫存超過一年",
  OK: "正常",
  "OK · 1 yr+ of stock": "正常 · 庫存一年以上",
  "Filter parts": "篩選零件",
  Export: "匯出",
  "✕ Back to needs attention": "✕ 返回需留意",
  "Couldn't load the forecast:": "無法載入預測：",
  "Loading…": "載入中…",
  Part: "零件",
  Status: "狀態",
  "Current\nstock": "目前\n庫存",
  "Forecast use": "預測用量",
  "red = more than stock": "紅色 = 超出庫存",
  "On order": "已下單",
  "not here yet": "尚未到貨",
  "Runs out": "缺貨日期",
  "at the forecast rate": "依預測用量計算",
  "Need to\norder": "需\n訂購",
  "Nothing needs attention in this category.": "此類別沒有需要留意的零件。",
  "Nothing matches.": "沒有符合的結果。",
  "chase it · no order needed": "請追蹤 · 無需下單",
  "Everything else · {n}": "其他 · {n}",
  "Show them": "顯示",
  "Top-ups and OK parts. Pick a line to list its parts.": "補貨與正常的零件。選擇一個產品線查看其零件。",
  "Top-ups and OK parts.": "補貨與正常的零件。",
  "Quantities in {unit}. Click a part for its stock chart and how the order is worked out.": "數量單位：{unit}。點選零件可查看庫存圖表及訂購數量的計算方式。",
  "{n} parts with no stock, orders or forecast aren't listed.": "另有 {n} 個無庫存、無訂單、無預測的零件未列出。",
  "6+ months": "6 個月以上",

  // ─── stock story and on-order cells ───
  "No use expected": "預計無用量",
  "Empty now": "目前缺貨",
  "Runs out {date}": "{date} 缺貨",
  "stock back {date}": "{date} 恢復庫存",
  "back after {month}": "{month}之後恢復",
  "nothing on order": "無在途訂單",
  "Below safety stock now": "目前低於安全庫存",
  "Below safety stock from {date}": "{date} 起低於安全庫存",
  "In stock all 6 months": "未來 6 個月庫存充足",
  late: "逾期",
  "{n} POs": "{n} 個 PO",
  "{n} more than current stock": "比目前庫存多 {n}",
  "Current stock covers it": "目前庫存足夠",
  "+{n} more": "另有 {n} 批",

  // ─── change password ───
  "Change password": "變更密碼",
  "Choose your password": "設定您的密碼",
  "You signed in with a one-time password. Choose your own to open the site.": "您使用一次性密碼登入。請設定自己的密碼後再使用本網站。",
  "One-time password": "一次性密碼",
  "Current password": "目前密碼",
  "New password": "新密碼",
  "(at least 10 characters)": "（至少 10 個字元）",
  "New password again": "再次輸入新密碼",
  "Saving…": "儲存中…",
  "Password changed.": "密碼已變更。",
  "The new passwords don't match.": "兩次輸入的新密碼不一致。",
  "Current password is wrong": "目前密碼錯誤",
  "At least 10 characters": "至少 10 個字元",
  "Can't be your email": "不能與電子郵件相同",
  "Choose a password different from the current one": "新密碼不能與目前密碼相同",
  "This account signs in with Microsoft": "此帳號使用 Microsoft 登入",
  "This account signs in with Microsoft. Change its password with Microsoft.": "此帳號使用 Microsoft 登入，請至 Microsoft 變更密碼。",
  "For signing in with email and password.": "用於電子郵件與密碼登入。",
  "Back to Home": "返回首頁",
};
